import { selectCurrentPageId, selectScenarioId, useAppSelector } from '@libs/store';
import { TUseCache, TWidget } from '@libs/types';
import { useCallback, useEffect } from 'react';

const cachePrefix = 'ps3_';

export const useCache = <TD>(widget: TWidget<TD>): TUseCache<TD> => {
    const scenarioId = useAppSelector(selectScenarioId);
    const currentPageId = useAppSelector(selectCurrentPageId);

    const key = widget.internalId || widget.uid;

    const cacheName = `${cachePrefix}${scenarioId}_${currentPageId}`;
    const expiryTime = 30;

    useEffect(() => {
        caches.keys().then((keys) => {
            if (Array.isArray(keys)) {
                keys.filter((key) => key.startsWith(cachePrefix))
                    .filter((k) => k !== cacheName)
                    .forEach((k) => {
                        caches.delete(k);
                    });
            }
        });
    }, [cacheName]);

    return {
        putCache: useCallback(
            async (data: TD) => {
                const cache = await caches.open(cacheName);
                const expiry = Date.now() + expiryTime * 60 * 1000;

                const response = new Response(JSON.stringify({ data, expiry }), {
                    headers: { 'Content-Type': 'application/json', 'Cache-Control': 'max-age=3600' },
                });

                await cache.put(key, response);
            },
            [cacheName, key],
        ),
        getCache: useCallback(async () => {
            const cache = await caches.open(cacheName);
            const response = await cache.match(key);

            if (!response) return null;

            const cachedData = await response.json();
            const now = Date.now();

            if (now < cachedData.expiry) {
                return cachedData.data;
            } else {
                // Если время истекло, удаляем элемент
                await cache.delete(key);
                return null;
            }
        }, [cacheName, key]),
        clearCache: useCallback(async () => {
            const cache = await caches.open(cacheName);
            await cache.delete(key);
        }, [cacheName, key]),
    };
};
